/**
 * Utilitário de impressão de documentos para Montagem de Paletes
 * Gera documentos formatados em HTML para impressão via window.print()
 */

import { Config, getToken, getUser } from '../services/config';
import type { MontagemPaleteOperacao, PaleteItem } from '../types/montagemPalete';
import { buscarPaleteDetalhado as fetchPaleteDetalhadoApi } from '../services/montagemPalete';
import QRCode from 'qrcode';

// =============================================
// HELPERS
// =============================================

/**
 * Calcula a quantidade de volumes removidos únicos de um pedido
 * que NÃO possuem uma leitura 'ok' correspondente.
 * Usa codigo_lido (sempre presente e confiável) ao invés de depender
 * da extração de número de volume dos dados decodificados.
 */
function calcularRemovidosUnicos(etiquetas: { codigo_lido: string; resultado: string }[]): number {
  const codigosOk = new Set<string>();
  const codigosRemovidos = new Set<string>();
  for (const e of etiquetas) {
    if (e.resultado === 'ok' && e.codigo_lido) codigosOk.add(e.codigo_lido);
    if (e.resultado === 'removido' && e.codigo_lido) codigosRemovidos.add(e.codigo_lido);
  }
  let count = 0;
  codigosRemovidos.forEach(c => { if (!codigosOk.has(c)) count++; });
  return count;
}

/**
 * Calcula a quantidade efetiva de volumes faltantes de um pedido,
 * descontando volumes que foram removidos intencionalmente.
 */
function calcularFaltantesEfetivos(pd: { total_volumes_esperado: number; total_lido: number; etiquetas: { codigo_lido: string; resultado: string }[] }): number {
  return Math.max(0, pd.total_volumes_esperado - pd.total_lido - calcularRemovidosUnicos(pd.etiquetas));
}

// =============================================
// TIPOS E INTERFACES
// =============================================

interface EmpresaData {
  razao_social: string;
  cnpj: string;
  logomarca_base64?: string;
}

interface PrintConfig {
  operacao: MontagemPaleteOperacao;
  paletes: PaleteItem[];
}

interface PaleteDetalhadoEtiqueta {
  id: number;
  codigo_lido: string;
  resultado: string;
  tipo_leitura: string;
  dados_decodificados: Record<string, unknown>;
  lido_em: string;
  criado_em: string;
  conferencia_id: number;
}

interface PaleteDetalhadoPedido {
  numero_pedido: string;
  total_volumes_esperado: number;
  total_lido: number;
  total_duplicados: number;
  total_removidos: number;
  volumes_removidos_unicos: number;
  etiquetas: PaleteDetalhadoEtiqueta[];
}

interface PaleteDetalhadoOcorrencia {
  id: number;
  tipo_ocorrencia_id: number;
  tipo_nome: string;
  categoria: string;
  justificativa: string;
  quantidade_divergente: number;
  criado_em: string;
  numero_pedido: string;
}

interface EtiquetaPendente {
  id: number;
  codigo_barras: string | null;
  numero_pedido: string;
  volume_atual: number | null;
  total_volumes_pedido: number | null;
  status: string;
  numero_nfe: string | null;
  destinatario_nome: string | null;
  destinatario_cnpj: string | null;
}

interface PaleteDetalhado {
  id: number;
  numero_palete: number;
  operacao_id: number;
  area_entrega_id: number;
  doca: string | null;
  tipo_palete: string;
  data_agendamento: string | null;
  observacoes: string | null;
  criado_em: string;
  atualizado_em: string;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  numero_operacao: number;
  operacao_status: string;
  cliente_id: number;
  cliente_razao_social: string;
  cliente_cnpj: string;
  resumo: {
    total_leituras: number;
    leituras_ok: number;
    leituras_duplicadas: number;
    leituras_removidas: number;
    peso_total: number;
    total_pedidos: number;
  };
  pedidos: PaleteDetalhadoPedido[];
  ocorrencias: PaleteDetalhadoOcorrencia[];
  etiquetas_pendentes: EtiquetaPendente[];
}

// =============================================
// FUNÇÕES AUXILIARES
// =============================================

const fetchPaletesDetalhados = async (paleteIds: number[]): Promise<PaleteDetalhado[]> => {
  try {
    const results = await Promise.all(
      paleteIds.map(async (id) => {
        try {
          const result = await fetchPaleteDetalhadoApi(id);
          return (result.palete as unknown as PaleteDetalhado) || null;
        } catch {
          return null;
        }
      })
    );
    return results.filter((p): p is PaleteDetalhado => p !== null);
  } catch {
    return [];
  }
};

const fetchEmpresaData = async (): Promise<EmpresaData | null> => {
  try {
    const token = getToken();
    if (!token) return null;
    const response = await fetch(`${Config.API_BASE_URL}/empresas/dados-impressao`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    if (response.ok) {
      const result = await response.json();
      return result.data || null;
    }
    return null;
  } catch {
    return null;
  }
};

const formatarCNPJ = (cnpj: string): string => {
  if (!cnpj) return '';
  const cleaned = String(cnpj).replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR');
const formatTime = (d: string) => new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const formatDateTime = (d: string) => `${formatDate(d)} ${formatTime(d)}`;

const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  em_conferencia: 'Em Conferência',
  em_carregamento: 'Em Carregamento',
  em_transito: 'Em Trânsito',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const LOCAL_LABELS: Record<string, string> = {
  cliente: 'No Cliente',
  base_transportadora: 'Base da Transportadora'
};

const openPrintWindow = (html: string) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Não foi possível abrir a janela de impressão. Verifique se pop-ups estão permitidos.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 400);
  };
};

// =============================================
// ESTILOS COMPARTILHADOS
// =============================================

const baseStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    color: #333;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.2rem;
    padding-bottom: 0.8rem;
    border-bottom: 2px solid #04364F;
  }
  .print-header-left { flex: 0 0 100px; display: flex; align-items: center; }
  .print-header-left img { max-width: 90px; max-height: 55px; object-fit: contain; }
  .print-header-center { flex: 1; text-align: center; padding: 0 16px; }
  .print-header-center h1 { color: #04364F; font-size: 1.3rem; font-weight: 700; margin: 0; }
  .print-header-center .subtitle { color: #666; font-size: 0.85rem; margin-top: 2px; }
  .print-header-right { flex: 0 0 180px; text-align: right; }
  .print-header-right p { color: #666; font-size: 0.75rem; margin: 0.15rem 0; }
  .print-header-right strong { color: #04364F; }
  .info-bar {
    display: flex; gap: 24px; flex-wrap: wrap;
    background: #f8f9fa; border-radius: 6px; padding: 10px 14px;
    margin-bottom: 1rem; font-size: 0.8rem;
  }
  .info-bar .info-item { display: flex; gap: 4px; }
  .info-bar .info-label { color: #7f8c8d; font-weight: 600; }
  .info-bar .info-val { color: #2c3e50; font-weight: 500; }
  table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  thead { background: #04364F; color: white; }
  th { padding: 8px 6px; text-align: left; font-weight: 600; border: 1px solid #04364F; }
  td { padding: 6px; border: 1px solid #ddd; }
  tbody tr:nth-child(even) { background: #f8f9fa; }
  .print-footer {
    margin-top: 2rem; text-align: center; font-size: 0.7rem;
    color: #666; border-top: 1px solid #ddd; padding-top: 8px;
  }
  .print-footer strong { color: #04364F; }
  @media print {
    body { margin: 0; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
  }
`;

const buildHeader = (
  title: string,
  subtitle: string,
  logoSrc: string,
  usuario: string,
  dataHora: string
) => `
  <div class="print-header">
    <div class="print-header-left">
      <img src="${logoSrc}" alt="Logo" onerror="this.style.display='none'" />
    </div>
    <div class="print-header-center">
      <h1>${title}</h1>
      <div class="subtitle">${subtitle}</div>
    </div>
    <div class="print-header-right">
      <p><strong>Emitido por:</strong> ${usuario}</p>
      <p><strong>Data/Hora:</strong> ${dataHora}</p>
    </div>
  </div>
`;

const buildInfoBar = (operacao: MontagemPaleteOperacao) => `
  <div class="info-bar">
    <div class="info-item"><span class="info-label">Operação:</span><span class="info-val">#${operacao.numero_operacao}</span></div>
    <div class="info-item"><span class="info-label">Cliente:</span><span class="info-val">${operacao.cliente_razao_social || '-'}</span></div>
    <div class="info-item"><span class="info-label">Filial:</span><span class="info-val">${operacao.filial_nome || '-'}</span></div>
    <div class="info-item"><span class="info-label">Local:</span><span class="info-val">${LOCAL_LABELS[operacao.local_pre_carregamento] || '-'}</span></div>
    <div class="info-item"><span class="info-label">Status:</span><span class="info-val">${STATUS_LABELS[operacao.status] || operacao.status}</span></div>
    <div class="info-item"><span class="info-label">Emissão:</span><span class="info-val">${formatDateTime(operacao.data_emissao)}</span></div>
    ${operacao.conferente_nome ? `<div class="info-item"><span class="info-label">Conferente:</span><span class="info-val">${operacao.conferente_nome}</span></div>` : ''}
  </div>
`;

const buildFooter = (empresa: EmpresaData | null) => `
  <div class="print-footer">
    <p><strong>${empresa?.razao_social || ''}</strong>${empresa?.cnpj ? ` | CNPJ: ${formatarCNPJ(empresa.cnpj)}` : ''}</p>
    <p>Documento gerado pelo sistema ConfereAí em ${formatDateTime(new Date().toISOString())}</p>
  </div>
`;

const buildDoc = (styles: string, body: string) => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      <style>${baseStyles}${styles}</style>
    </head>
    <body>${body}</body>
  </html>
`;

// =============================================
// DOCUMENTO 1: ROMANEIO DE PALETES
// =============================================

export const printRomaneio = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados dos paletes
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  // Agrupar paletes por área
  const areaMap = new Map<string, PaleteItem[]>();
  paletes.forEach(p => {
    const key = p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `Área #${p.area_entrega_id}`);
    if (!areaMap.has(key)) areaMap.set(key, []);
    areaMap.get(key)!.push(p);
  });

  // Totalizadores
  let totalLeituras = 0, totalOk = 0, totalDuplicadas = 0, totalPedidos = 0, totalOcorrencias = 0;
  let pesoTotal = 0;
  detalhados.forEach(d => {
    totalLeituras += d.resumo?.total_leituras || 0;
    totalOk += d.resumo?.leituras_ok || 0;
    totalDuplicadas += d.resumo?.leituras_duplicadas || 0;
    totalPedidos += d.resumo?.total_pedidos || 0;
    totalOcorrencias += d.ocorrencias?.length || 0;
    pesoTotal += d.resumo?.peso_total || 0;
  });

  const rows = paletes.map((p, i) => {
    const det = detalheMap.get(p.id);
    const numOcorrencias = det?.ocorrencias?.length || 0;
    const temOcorrencia = numOcorrencias > 0;
    return `
    <tr${temOcorrencia ? ' style="background:#fff8f0"' : ''}>
      <td style="text-align:center;font-weight:600">${i + 1}</td>
      <td style="text-align:center;font-weight:700;font-size:1rem">${p.numero_palete}</td>
      <td>${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}</td>
      <td>${p.area_regiao || '-'}</td>
      <td>${p.doca || '-'}</td>
      <td style="text-align:center">${p.tipo_palete === 'agendamento'
        ? '<span style="background:#fde8d0;color:#a04000;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Agendamento</span>'
        : '<span style="background:#d1ecf1;color:#0c5460;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Normal</span>'}</td>
      <td style="text-align:center">${det?.resumo?.total_pedidos || 0}</td>
      <td style="text-align:center">${det?.resumo?.leituras_ok || 0}/${det?.resumo?.total_leituras || 0}${(det?.resumo?.leituras_duplicadas || 0) > 0 ? ` <span style="color:#e67e22;font-size:0.7rem">(${det?.resumo?.leituras_duplicadas} dup.)</span>` : ''}</td>
      <td style="text-align:center">${det?.resumo?.peso_total ? det.resumo.peso_total.toFixed(1) + ' kg' : '-'}</td>
      <td style="text-align:center">${temOcorrencia
        ? `<span style="background:#f8d7da;color:#721c24;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">${numOcorrencias}</span>`
        : '<span style="color:#27ae60;font-size:0.75rem">0</span>'}</td>
      <td>${p.observacoes || '-'}</td>
    </tr>
  `}).join('');

  const styles = `
    @page { size: A4 landscape; margin: 1.2cm; }
    .summary { display: flex; gap: 12px; margin-bottom: 1rem; flex-wrap: wrap; }
    .summary-card {
      flex: 1; min-width: 100px; background: #f0f4f8; border-radius: 6px; padding: 10px;
      text-align: center; border: 1px solid #dce1e6;
    }
    .summary-card .label { font-size: 0.65rem; color: #7f8c8d; font-weight: 600; text-transform: uppercase; }
    .summary-card .value { font-size: 1.2rem; font-weight: 700; color: #2c3e50; }
    .summary-card.alert { border-color: #e74c3c; background: #fdf0f0; }
    .summary-card.alert .value { color: #e74c3c; }
    .summary-card.success { border-color: #27ae60; background: #f0fdf4; }
    .summary-card.success .value { color: #27ae60; }
  `;

  const body = `
    ${buildHeader('Romaneio de Paletes', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}
    <div class="summary">
      <div class="summary-card"><div class="label">Total de Paletes</div><div class="value">${paletes.length}</div></div>
      <div class="summary-card"><div class="label">Áreas Distintas</div><div class="value">${areaMap.size}</div></div>
      <div class="summary-card"><div class="label">Docas Utilizadas</div><div class="value">${new Set(paletes.filter(p => p.doca).map(p => p.doca)).size}</div></div>
      <div class="summary-card"><div class="label">Total Pedidos</div><div class="value">${totalPedidos}</div></div>
      <div class="summary-card${totalOk > 0 ? ' success' : ''}"><div class="label">Etiquetas OK</div><div class="value">${totalOk}/${totalLeituras}</div></div>
      ${totalDuplicadas > 0 ? `<div class="summary-card alert"><div class="label">Duplicadas</div><div class="value">${totalDuplicadas}</div></div>` : ''}
      ${pesoTotal > 0 ? `<div class="summary-card"><div class="label">Peso Total</div><div class="value">${pesoTotal.toFixed(1)} kg</div></div>` : ''}
      ${totalOcorrencias > 0 ? `<div class="summary-card alert"><div class="label">Ocorrências</div><div class="value">${totalOcorrencias}</div></div>` : ''}
    </div>
    <table>
      <thead>
        <tr>
          <th style="width:30px;text-align:center">#</th>
          <th style="width:55px;text-align:center">Palete</th>
          <th>Área de Entrega</th>
          <th>Região</th>
          <th>Doca</th>
          <th style="width:80px;text-align:center">Tipo</th>
          <th style="width:60px;text-align:center">Pedidos</th>
          <th style="width:100px;text-align:center">Etiquetas</th>
          <th style="width:65px;text-align:center">Peso</th>
          <th style="width:60px;text-align:center">Ocorr.</th>
          <th>Obs.</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// DOCUMENTO 2: MAPA DE CARREGAMENTO
// =============================================

export const printMapaCarregamento = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados dos paletes
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  // Agrupar por doca
  const docaMap = new Map<string, PaleteItem[]>();
  paletes.forEach(p => {
    const doca = p.doca || 'SEM DOCA';
    if (!docaMap.has(doca)) docaMap.set(doca, []);
    docaMap.get(doca)!.push(p);
  });

  const docasHtml = Array.from(docaMap.entries()).map(([doca, items]) => {
    // Totais da doca
    let docaPedidos = 0, docaLeituras = 0, docaOk = 0, docaOcorrencias = 0;
    items.forEach(p => {
      const det = detalheMap.get(p.id);
      if (det) {
        docaPedidos += det.resumo?.total_pedidos || 0;
        docaLeituras += det.resumo?.total_leituras || 0;
        docaOk += det.resumo?.leituras_ok || 0;
        docaOcorrencias += det.ocorrencias?.length || 0;
      }
    });
    return `
    <div class="doca-section">
      <div class="doca-header">
        <span class="doca-name"><i class="fas fa-warehouse"></i> ${doca}</span>
        <span class="doca-stats">
          ${items.length} palete${items.length > 1 ? 's' : ''}
          ${docaPedidos > 0 ? ` · ${docaPedidos} pedidos` : ''}
          ${docaLeituras > 0 ? ` · ${docaOk}/${docaLeituras} etiquetas` : ''}
          ${docaOcorrencias > 0 ? ` · <span style="color:#ffcccc">${docaOcorrencias} ocorr.</span>` : ''}
        </span>
      </div>
      <div class="doca-paletes">
        ${items.map(p => {
          const det = detalheMap.get(p.id);
          const numOcorrencias = det?.ocorrencias?.length || 0;
          const temOcorrencia = numOcorrencias > 0;
          return `
          <div class="doca-palete${p.tipo_palete === 'agendamento' ? ' agendamento' : ''}${temOcorrencia ? ' com-ocorrencia' : ''}">
            <div class="palete-num">#${p.numero_palete}</div>
            <div class="palete-area">${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `Área #${p.area_entrega_id}`)}</div>
            ${p.area_regiao ? `<div class="palete-regiao">${p.area_regiao}</div>` : ''}
            ${det ? `
              <div class="palete-stats">
                ${(det.resumo?.total_pedidos || 0) > 0 ? `<span class="ps-item"><i class="fas fa-box"></i> ${det.resumo.total_pedidos} ped.</span>` : ''}
                ${(det.resumo?.total_leituras || 0) > 0 ? `<span class="ps-item ${det.resumo.leituras_ok === det.resumo.total_leituras ? 'ok' : ''}"><i class="fas fa-barcode"></i> ${det.resumo.leituras_ok}/${det.resumo.total_leituras}</span>` : ''}
                ${(det.resumo?.leituras_duplicadas || 0) > 0 ? `<span class="ps-item dup"><i class="fas fa-clone"></i> ${det.resumo.leituras_duplicadas}</span>` : ''}
                ${temOcorrencia ? `<span class="ps-item alert"><i class="fas fa-exclamation-triangle"></i> ${numOcorrencias}</span>` : ''}
                ${(det.resumo?.peso_total || 0) > 0 ? `<span class="ps-item"><i class="fas fa-weight-hanging"></i> ${det.resumo.peso_total.toFixed(1)}kg</span>` : ''}
              </div>
            ` : ''}
            ${p.tipo_palete === 'agendamento' ? `<div class="palete-tipo-tag">Agendamento${p.data_agendamento ? ' - ' + formatDateTime(p.data_agendamento) : ''}</div>` : ''}
          </div>
        `}).join('')}
      </div>
    </div>
  `}).join('');

  const styles = `
    @page { size: A4 landscape; margin: 1.5cm; }
    .doca-section { margin-bottom: 20px; page-break-inside: avoid; }
    .doca-header {
      display: flex; justify-content: space-between; align-items: center;
      background: #04364F; color: white; padding: 8px 14px; border-radius: 6px 6px 0 0;
      font-weight: 600; font-size: 0.9rem;
    }
    .doca-header i { margin-right: 6px; }
    .doca-stats { font-size: 0.8rem; opacity: 0.9; }
    .doca-paletes {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 8px; padding: 12px; background: #f8f9fa;
      border: 1px solid #dce1e6; border-top: none; border-radius: 0 0 6px 6px;
    }
    .doca-palete {
      background: white; border: 2px solid #dce1e6; border-radius: 8px;
      padding: 10px; text-align: center;
    }
    .doca-palete.com-ocorrencia { border-color: #e74c3c; background: #fff8f8; }
    .doca-palete .palete-num { font-size: 1.3rem; font-weight: 700; color: #3498db; }
    .doca-palete .palete-area { font-size: 0.8rem; font-weight: 600; color: #2c3e50; margin-top: 4px; }
    .doca-palete .palete-regiao { font-size: 0.7rem; color: #7f8c8d; margin-top: 2px; }
    .doca-palete.agendamento { border-color: #e67e22; background: #fef9f0; }
    .doca-palete .palete-tipo-tag { font-size: 0.65rem; color: #a04000; background: #fde8d0; padding: 1px 6px; border-radius: 3px; margin-top: 3px; font-weight: 600; }
    .palete-stats { display: flex; flex-wrap: wrap; gap: 4px; justify-content: center; margin-top: 6px; }
    .ps-item { font-size: 0.65rem; padding: 1px 5px; border-radius: 3px; background: #f0f4f8; color: #2c3e50; font-weight: 600; }
    .ps-item i { margin-right: 2px; font-size: 0.6rem; }
    .ps-item.ok { background: #d4edda; color: #155724; }
    .ps-item.dup { background: #fff3cd; color: #856404; }
    .ps-item.alert { background: #f8d7da; color: #721c24; }
  `;

  const body = `
    ${buildHeader('Mapa de Carregamento', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}
    ${docasHtml}
    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// DOCUMENTO 3: ETIQUETAS DE PALETES
// =============================================

export const printEtiquetas = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  const etiquetas = paletes.map(p => {
    const det = detalheMap.get(p.id);
    const numOcorrencias = det?.ocorrencias?.length || 0;
    const temOcorrencia = numOcorrencias > 0;
    // Lista de pedidos
    const pedidos = det?.pedidos || [];
    const pedidosStr = pedidos.length > 0
      ? pedidos.map(pd => pd.numero_pedido).join(', ')
      : '-';

    return `
    <div class="etiqueta-page">
      <div class="etiqueta${temOcorrencia ? ' com-ocorrencia' : ''}">
        <div class="etiqueta-header">
          <div class="etiqueta-header-left">
            <img src="${logoSrc}" alt="" class="etiqueta-logo" onerror="this.style.display='none'" />
            <div class="etiqueta-empresa">${empresa?.razao_social || 'ConfereAí'}</div>
          </div>
          <div class="etiqueta-header-right">
            <div class="etiqueta-op">OPERAÇÃO #${operacao.numero_operacao}</div>
            <div class="etiqueta-date">${formatDate(operacao.data_emissao)}</div>
          </div>
        </div>

        <div class="etiqueta-body">
          <div class="etiqueta-left-col">
            <div class="etiqueta-num">PALETE</div>
            <div class="etiqueta-num-big">#${p.numero_palete}</div>
            ${p.tipo_palete === 'agendamento'
              ? '<div class="etiqueta-tipo-badge agend">AGENDAMENTO</div>'
              : '<div class="etiqueta-tipo-badge normal">NORMAL</div>'}
          </div>
          <div class="etiqueta-right-col">
            <div class="etiqueta-detail"><span class="lbl">ÁREA DE ENTREGA</span><span class="val">${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}</span></div>
            ${p.area_regiao ? `<div class="etiqueta-detail"><span class="lbl">REGIÃO</span><span class="val">${p.area_regiao}</span></div>` : ''}
            ${p.doca ? `<div class="etiqueta-detail"><span class="lbl">DOCA</span><span class="val">${p.doca}</span></div>` : ''}
            <div class="etiqueta-detail"><span class="lbl">CLIENTE</span><span class="val" style="font-size:1rem">${operacao.cliente_razao_social || '-'}</span></div>
            ${operacao.filial_nome ? `<div class="etiqueta-detail"><span class="lbl">FILIAL</span><span class="val" style="font-size:1rem">${operacao.filial_nome}</span></div>` : ''}
            ${p.tipo_palete === 'agendamento' && p.data_agendamento ? `<div class="etiqueta-detail"><span class="lbl">DATA AGENDAMENTO</span><span class="val" style="font-size:1rem;color:#a04000">${formatDateTime(p.data_agendamento)}</span></div>` : ''}
          </div>
        </div>

        <div class="etiqueta-stats-bar">
          <div class="stat-box"><div class="stat-label">PEDIDOS</div><div class="stat-value">${det?.resumo?.total_pedidos || 0}</div></div>
          <div class="stat-box"><div class="stat-label">ETIQUETAS LIDAS</div><div class="stat-value">${det?.resumo?.total_leituras || 0}</div></div>
          <div class="stat-box success"><div class="stat-label">ETIQUETAS OK</div><div class="stat-value">${det?.resumo?.leituras_ok || 0}</div></div>
          <div class="stat-box${(det?.resumo?.leituras_duplicadas || 0) > 0 ? ' warn' : ''}"><div class="stat-label">DUPLICADAS</div><div class="stat-value">${det?.resumo?.leituras_duplicadas || 0}</div></div>
          ${(det?.resumo?.peso_total || 0) > 0 ? `<div class="stat-box"><div class="stat-label">PESO</div><div class="stat-value">${det!.resumo.peso_total.toFixed(1)} kg</div></div>` : ''}
          <div class="stat-box${temOcorrencia ? ' danger' : ''}"><div class="stat-label">OCORRÊNCIAS</div><div class="stat-value">${numOcorrencias}</div></div>
        </div>

        ${pedidos.length > 0 ? `
        <div class="etiqueta-pedidos">
          <div class="pedidos-title">PEDIDOS VINCULADOS</div>
          <div class="pedidos-list">${pedidosStr}</div>
        </div>
        ` : ''}

        <div class="etiqueta-footer">
          <div class="footer-left">${operacao.conferente_nome ? `Conferente: ${operacao.conferente_nome}` : ''}</div>
          <div class="footer-right">Documento gerado em ${formatDateTime(new Date().toISOString())}</div>
        </div>
      </div>
    </div>
  `}).join('');

  const styles = `
    @page { size: A4 landscape; margin: 0; }
    html, body { width: 100%; padding: 0; margin: 0; }
    .etiqueta-page {
      page-break-after: always; width: 100%; height: 100vh;
      display: flex; padding: 0; margin: 0;
    }
    .etiqueta-page:last-child { page-break-after: auto; }
    .etiqueta {
      border: 5px solid #04364F; padding: 18px 24px;
      width: 100%; height: 100%; display: flex; flex-direction: column;
    }
    .etiqueta.com-ocorrencia { border-color: #e74c3c; }
    .etiqueta-header {
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 4px solid #04364F; padding-bottom: 8px; margin-bottom: 8px;
    }
    .com-ocorrencia .etiqueta-header { border-bottom-color: #e74c3c; }
    .etiqueta-header-left { display: flex; align-items: center; gap: 12px; }
    .etiqueta-logo { max-width: 90px; max-height: 55px; object-fit: contain; }
    .etiqueta-empresa { font-size: 1.4rem; font-weight: 700; color: #04364F; }
    .etiqueta-header-right { text-align: right; }
    .etiqueta-op { font-size: 1.8rem; font-weight: 900; color: #04364F; }
    .etiqueta-date { font-size: 1.1rem; color: #444; font-weight: 600; }

    .etiqueta-body { display: flex; gap: 24px; flex: 1; margin-bottom: 8px; }
    .etiqueta-left-col {
      flex: 0 0 300px; display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      border-right: 4px dashed #bbb; padding-right: 24px;
    }
    .etiqueta-num { font-size: 1.6rem; font-weight: 800; color: #7f8c8d; letter-spacing: 4px; }
    .etiqueta-num-big { font-size: 10rem; font-weight: 900; color: #04364F; line-height: 1; margin: 4px 0; }
    .com-ocorrencia .etiqueta-num-big { color: #e74c3c; }
    .etiqueta-tipo-badge {
      padding: 6px 28px; border-radius: 8px; font-weight: 800;
      font-size: 1.3rem; letter-spacing: 3px; margin-top: 8px;
    }
    .etiqueta-tipo-badge.normal { background: #d1ecf1; color: #0c5460; }
    .etiqueta-tipo-badge.agend { background: #fde8d0; color: #a04000; }

    .etiqueta-right-col { flex: 1; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
    .etiqueta-detail { display: flex; flex-direction: column; gap: 2px; }
    .etiqueta-detail .lbl { font-size: 1rem; font-weight: 700; color: #7f8c8d; letter-spacing: 1px; }
    .etiqueta-detail .val { font-size: 2rem; font-weight: 800; color: #2c3e50; }

    .etiqueta-stats-bar {
      display: flex; gap: 12px; padding: 10px 0; border-top: 4px solid #ddd;
      border-bottom: 4px solid #ddd; margin-bottom: 8px;
    }
    .stat-box {
      flex: 1; text-align: center; background: #f8f9fa; border-radius: 8px;
      padding: 10px 8px; border: 2px solid #e0e0e0;
    }
    .stat-box.success { background: #d4edda; border-color: #c3e6cb; }
    .stat-box.warn { background: #fff3cd; border-color: #ffc107; }
    .stat-box.danger { background: #f8d7da; border-color: #f5c6cb; }
    .stat-label { font-size: 0.9rem; font-weight: 700; color: #7f8c8d; letter-spacing: 0.5px; }
    .stat-value { font-size: 1.6rem; font-weight: 900; color: #2c3e50; }
    .stat-box.success .stat-value { color: #155724; }
    .stat-box.warn .stat-value { color: #856404; }
    .stat-box.danger .stat-value { color: #721c24; }

    .etiqueta-pedidos { margin-bottom: 4px; }
    .pedidos-title { font-size: 1rem; font-weight: 700; color: #7f8c8d; margin-bottom: 4px; letter-spacing: 1px; }
    .pedidos-list { font-size: 1.1rem; color: #2c3e50; font-weight: 600; word-break: break-all; }

    .etiqueta-footer {
      display: flex; justify-content: space-between;
      font-size: 0.9rem; color: #777; margin-top: auto;
    }
  `;

  const body = etiquetas;
  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// DOCUMENTO 4: RESUMO DA OPERAÇÃO
// =============================================

export const printResumo = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  // Totalizadores gerais
  let totalLeituras = 0, totalOk = 0, totalDuplicadas = 0, totalRemovidas = 0;
  let totalPedidos = 0, totalOcorrencias = 0, pesoTotal = 0;
  detalhados.forEach(d => {
    totalLeituras += d.resumo?.total_leituras || 0;
    totalOk += d.resumo?.leituras_ok || 0;
    totalDuplicadas += d.resumo?.leituras_duplicadas || 0;
    totalRemovidas += d.resumo?.leituras_removidas || 0;
    totalPedidos += d.resumo?.total_pedidos || 0;
    totalOcorrencias += d.ocorrencias?.length || 0;
    pesoTotal += d.resumo?.peso_total || 0;
  });

  // Conferidos e progresso total (baseado em etiquetas bipadas OK vs total de etiquetas)
  const progressoTotal = totalLeituras > 0 ? (totalOk / totalLeituras) * 100 : 0;
  const progressoTotalColor = progressoTotal === 100 ? '#27ae60' : progressoTotal >= 75 ? '#f39c12' : '#e74c3c';

  // Estatísticas por área (com dados detalhados)
  const areaStats = new Map<string, { count: number; regiao: string; pedidos: number; leituras: number; ok: number; duplicadas: number; ocorrencias: number; peso: number }>();
  paletes.forEach(p => {
    const nome = p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `Área #${p.area_entrega_id}`);
    const det = detalheMap.get(p.id);
    if (!areaStats.has(nome)) areaStats.set(nome, { count: 0, regiao: p.area_regiao || '-', pedidos: 0, leituras: 0, ok: 0, duplicadas: 0, ocorrencias: 0, peso: 0 });
    const stat = areaStats.get(nome)!;
    stat.count++;
    if (det) {
      stat.pedidos += det.resumo.total_pedidos || 0;
      stat.leituras += det.resumo.total_leituras || 0;
      stat.ok += det.resumo.leituras_ok || 0;
      stat.duplicadas += det.resumo.leituras_duplicadas || 0;
      stat.ocorrencias += (det.ocorrencias?.length) || 0;
      stat.peso += det.resumo.peso_total || 0;
    }
  });

  // Estatísticas por doca
  const docaStats = new Map<string, { count: number; pedidos: number; leituras: number; ok: number; ocorrencias: number }>();
  paletes.forEach(p => {
    const doca = p.doca || 'Sem doca';
    const det = detalheMap.get(p.id);
    if (!docaStats.has(doca)) docaStats.set(doca, { count: 0, pedidos: 0, leituras: 0, ok: 0, ocorrencias: 0 });
    const stat = docaStats.get(doca)!;
    stat.count++;
    if (det) {
      stat.pedidos += det.resumo.total_pedidos || 0;
      stat.leituras += det.resumo.total_leituras || 0;
      stat.ok += det.resumo.leituras_ok || 0;
      stat.ocorrencias += (det.ocorrencias?.length) || 0;
    }
  });

  // Agrupar ocorrências por categoria
  const ocorrCategorias = new Map<string, { count: number; qtd: number }>();
  detalhados.forEach(d => {
    (d.ocorrencias || []).forEach(o => {
      const cat = o.categoria || o.tipo_nome || 'Outros';
      if (!ocorrCategorias.has(cat)) ocorrCategorias.set(cat, { count: 0, qtd: 0 });
      const c = ocorrCategorias.get(cat)!;
      c.count++;
      c.qtd += o.quantidade_divergente || 0;
    });
  });

  const areaRows = Array.from(areaStats.entries())
    .sort((a, b) => {
      const progA = a[1].leituras > 0 ? (a[1].ok / a[1].leituras) * 100 : 0;
      const progB = b[1].leituras > 0 ? (b[1].ok / b[1].leituras) * 100 : 0;
      return progB - progA;
    })
    .map(([nome, stat]) => {
    const progresso = stat.leituras > 0 ? (stat.ok / stat.leituras) * 100 : 0;
    const progColor = progresso === 100 ? '#27ae60' : progresso >= 75 ? '#f39c12' : '#e74c3c';
    return `
    <tr>
      <td>${nome}</td>
      <td>${stat.regiao}</td>
      <td style="text-align:center;font-weight:700">${stat.count}</td>
      <td style="text-align:center;font-weight:700">${stat.ok}/${stat.leituras}</td>
      <td style="text-align:center"><span style="color:${progColor};font-weight:700">${progresso.toFixed(2).replace('.', ',')}%</span></td>
      <td style="text-align:center">${stat.pedidos}</td>
      <td style="text-align:center;white-space:nowrap">${stat.ok}/${stat.leituras}${stat.duplicadas > 0 ? ` <span style="color:#e67e22;font-size:0.7rem">(${stat.duplicadas} dup.)</span>` : ''}</td>
      <td style="text-align:center">${stat.peso > 0 ? stat.peso.toFixed(1) + ' kg' : '-'}</td>
      <td style="text-align:center">${stat.ocorrencias > 0 ? `<span style="color:#e74c3c;font-weight:700">${stat.ocorrencias}</span>` : '0'}</td>
    </tr>
  `;}).join('');

  const docaRows = Array.from(docaStats.entries()).map(([doca, stat]) => `
    <tr>
      <td>${doca}</td>
      <td style="text-align:center;font-weight:700">${stat.count}</td>
      <td style="text-align:center">${stat.pedidos}</td>
      <td style="text-align:center">${stat.ok}/${stat.leituras}</td>
      <td style="text-align:center">${stat.ocorrencias > 0 ? `<span style="color:#e74c3c;font-weight:700">${stat.ocorrencias}</span>` : '0'}</td>
    </tr>
  `).join('');

  const ocorrRows = ocorrCategorias.size > 0 ? Array.from(ocorrCategorias.entries()).map(([cat, stat]) => `
    <tr><td>${cat}</td><td style="text-align:center;font-weight:700">${stat.count}</td><td style="text-align:center">${stat.qtd}</td></tr>
  `).join('') : '';

  const styles = `
    @page { size: A4 portrait; margin: 1.5cm; }
    .resumo-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 1rem; }
    .resumo-card {
      background: #f0f4f8; border: 1px solid #dce1e6; border-radius: 8px;
      padding: 12px; text-align: center;
    }
    .resumo-card.success { background: #d4edda; border-color: #c3e6cb; }
    .resumo-card.warn { background: #fff3cd; border-color: #ffc107; }
    .resumo-card.danger { background: #f8d7da; border-color: #f5c6cb; }
    .resumo-card .rc-label { font-size: 0.65rem; color: #7f8c8d; font-weight: 600; text-transform: uppercase; }
    .resumo-card .rc-value { font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin-top: 4px; }
    .resumo-card.success .rc-value { color: #155724; }
    .resumo-card.warn .rc-value { color: #856404; }
    .resumo-card.danger .rc-value { color: #721c24; }
    .section-title {
      font-size: 0.9rem; font-weight: 700; color: #04364F;
      border-bottom: 2px solid #04364F; padding-bottom: 4px; margin: 1.2rem 0 0.6rem;
    }
    .obs-box { background: #f8f9fa; border-radius: 6px; padding: 10px; margin-top: 8px; font-size: 0.85rem; }
  `;

  const body = `
    ${buildHeader('Resumo da Operação', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}

    <div class="resumo-grid">
      <div class="resumo-card"><div class="rc-label">Total de Paletes</div><div class="rc-value">${paletes.length}</div></div>
      <div class="resumo-card"><div class="rc-label">Áreas de Entrega</div><div class="rc-value">${areaStats.size}</div></div>
      <div class="resumo-card"><div class="rc-label">Tipo</div><div class="rc-value" style="font-size:1rem">${paletes.filter(p => p.tipo_palete !== 'agendamento').length} normais${paletes.filter(p => p.tipo_palete === 'agendamento').length > 0 ? ` / <span style="color:#a04000">${paletes.filter(p => p.tipo_palete === 'agendamento').length} agend.</span>` : ''}</div></div>
      <div class="resumo-card${progressoTotal === 100 ? ' success' : progressoTotal >= 75 ? ' warn' : ' danger'}"><div class="rc-label">Conferidos</div><div class="rc-value">${totalOk}/${totalLeituras} <span style="font-size:0.8rem">(${progressoTotal.toFixed(2).replace('.', ',')}%)</span></div></div>
      <div class="resumo-card"><div class="rc-label">Total de Pedidos</div><div class="rc-value">${totalPedidos}</div></div>
      <div class="resumo-card${totalOk > 0 ? ' success' : ''}"><div class="rc-label">Etiquetas OK</div><div class="rc-value">${totalOk}/${totalLeituras}</div></div>
      ${totalDuplicadas > 0 ? `<div class="resumo-card warn"><div class="rc-label">Etiq. Duplicadas</div><div class="rc-value">${totalDuplicadas}</div></div>` : ''}
      ${totalRemovidas > 0 ? `<div class="resumo-card"><div class="rc-label">Etiq. Removidas</div><div class="rc-value">${totalRemovidas}</div></div>` : ''}
      ${pesoTotal > 0 ? `<div class="resumo-card"><div class="rc-label">Peso Total</div><div class="rc-value">${pesoTotal.toFixed(1)} kg</div></div>` : ''}
      ${totalOcorrencias > 0 ? `<div class="resumo-card danger"><div class="rc-label">Ocorrências</div><div class="rc-value">${totalOcorrencias}</div></div>` : ''}
    </div>

    ${operacao.observacoes ? `<div class="obs-box"><strong>Observações:</strong> ${operacao.observacoes}</div>` : ''}

    <div class="section-title">Paletes por Área de Entrega</div>
    <table>
      <thead><tr><th>Área</th><th style="width:100px">Região</th><th style="width:60px;text-align:center">Paletes</th><th style="width:70px;text-align:center">Conferidos</th><th style="width:80px;text-align:center">Progresso</th><th style="width:60px;text-align:center">Pedidos</th><th style="width:140px;text-align:center;white-space:nowrap">Etiquetas</th><th style="width:70px;text-align:center">Peso</th><th style="width:60px;text-align:center">Ocorr.</th></tr></thead>
      <tbody>${areaRows}
        <tr style="font-weight:700;background:#f0f4f8;border-top:2px solid #04364F">
          <td colspan="2" style="text-align:right">Total</td>
          <td style="text-align:center">${paletes.length}</td>
          <td style="text-align:center">${totalOk}/${totalLeituras}</td>
          <td style="text-align:center"><span style="color:${progressoTotalColor};font-weight:700">${progressoTotal.toFixed(2).replace('.', ',')}%</span></td>
          <td style="text-align:center">${totalPedidos}</td>
          <td style="text-align:center">${totalOk}/${totalLeituras}</td>
          <td style="text-align:center">${pesoTotal > 0 ? pesoTotal.toFixed(1) + ' kg' : '-'}</td>
          <td style="text-align:center">${totalOcorrencias > 0 ? `<span style="color:#e74c3c">${totalOcorrencias}</span>` : '0'}</td>
        </tr>
      </tbody>
    </table>

    <div class="section-title">Paletes por Doca</div>
    <table>
      <thead><tr><th>Doca</th><th style="width:60px;text-align:center">Paletes</th><th style="width:60px;text-align:center">Pedidos</th><th style="width:100px;text-align:center">Etiquetas</th><th style="width:60px;text-align:center">Ocorr.</th></tr></thead>
      <tbody>${docaRows}</tbody>
    </table>

    ${ocorrCategorias.size > 0 ? `
    <div class="section-title">Ocorrências por Categoria</div>
    <table>
      <thead><tr><th>Categoria</th><th style="width:80px;text-align:center">Registros</th><th style="width:80px;text-align:center">Qtd. Divergente</th></tr></thead>
      <tbody>${ocorrRows}</tbody>
    </table>
    ` : ''}

    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// DOCUMENTO 5: CRACHÁ DE PALETE
// =============================================

export const printCrachaPalete = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  // Gerar QR Codes para cada palete
  const qrCodeMap = new Map<number, { paleteQr: string; areaQr: string }>();
  for (const p of paletes) {
    const paleteQr = await QRCode.toDataURL(String(p.numero_palete), { width: 120, margin: 1 });
    const areaCode = p.area_codigo || p.area_nome || `#${p.area_entrega_id}`;
    const areaQr = await QRCode.toDataURL(String(areaCode), { width: 120, margin: 1 });
    qrCodeMap.set(p.id, { paleteQr, areaQr });
  }

  const crachas = paletes.map(p => {
    const det = detalheMap.get(p.id);
    const numOcorrencias = det?.ocorrencias?.length || 0;
    const temOcorrencia = numOcorrencias > 0;
    const pedidos = det?.pedidos || [];
    const pedidosStr = pedidos.length > 0
      ? pedidos.map(pd => pd.numero_pedido).join(', ')
      : '';
    const qrCodes = qrCodeMap.get(p.id);

    return `
    <div class="cracha-page">
      <div class="cracha${temOcorrencia ? ' com-ocorrencia' : ''}">
        <div class="cracha-top">
          <div class="cracha-top-left">
            <img src="${logoSrc}" alt="" class="cracha-logo" onerror="this.style.display='none'" />
            <div class="cracha-empresa">${empresa?.razao_social || 'ConfereAí'}</div>
          </div>
          <div class="cracha-top-right">
            <div class="cracha-op">OP #${operacao.numero_operacao}</div>
            <div class="cracha-data">${formatDate(operacao.data_emissao)}</div>
          </div>
        </div>

        <div class="cracha-body">
          <div class="cracha-center">
            <div class="cracha-label-small">${p.tipo_palete === 'agendamento' ? 'AGENDAMENTO' : 'PALETE'}</div>
            <div class="cracha-number">${p.numero_palete}</div>
            <div class="cracha-tipo-badge ${p.tipo_palete === 'agendamento' ? 'agend' : 'normal'}">${p.tipo_palete === 'agendamento' ? 'AGENDAMENTO' : 'NORMAL'}</div>
            ${qrCodes ? `<img src="${qrCodes.paleteQr}" alt="QR Palete" class="cracha-qr cracha-qr-palete" />` : ''}
          </div>

          <div class="cracha-info-grid">
            <div class="cracha-info-box cracha-info-box-area">
              <span class="ci-label">ÁREA DE ENTREGA</span>
              <span class="ci-value">${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}</span>
              ${qrCodes ? `<img src="${qrCodes.areaQr}" alt="QR Área" class="cracha-qr cracha-qr-area" />` : ''}
            </div>
            ${p.area_regiao ? `
            <div class="cracha-info-box">
              <span class="ci-label">REGIÃO</span>
              <span class="ci-value">${p.area_regiao}</span>
            </div>` : ''}
            ${p.doca ? `
            <div class="cracha-info-box">
              <span class="ci-label">DOCA</span>
              <span class="ci-value">${p.doca}</span>
            </div>` : ''}
            <div class="cracha-info-box">
              <span class="ci-label">CLIENTE</span>
              <span class="ci-value" style="font-size:1.2rem">${operacao.cliente_razao_social || '-'}</span>
            </div>
            ${operacao.filial_nome ? `
            <div class="cracha-info-box">
              <span class="ci-label">FILIAL</span>
              <span class="ci-value" style="font-size:1.2rem">${operacao.filial_nome}</span>
            </div>` : ''}
            ${p.tipo_palete === 'agendamento' && p.data_agendamento ? `
            <div class="cracha-info-box">
              <span class="ci-label">DATA AGENDAMENTO</span>
              <span class="ci-value" style="color:#a04000">${formatDateTime(p.data_agendamento)}</span>
            </div>` : ''}
          </div>
        </div>

        <div class="cracha-stats">
          <div class="cs-item"><div class="cs-num">${det?.resumo?.total_pedidos || 0}</div><div class="cs-txt">Pedidos</div></div>
          <div class="cs-item"><div class="cs-num">${det?.resumo?.leituras_ok || 0}/${det?.resumo?.total_leituras || 0}</div><div class="cs-txt">Etiquetas</div></div>
          ${(det?.resumo?.leituras_duplicadas || 0) > 0 ? `<div class="cs-item warn"><div class="cs-num">${det!.resumo.leituras_duplicadas}</div><div class="cs-txt">Duplicadas</div></div>` : ''}
          ${(det?.resumo?.peso_total || 0) > 0 ? `<div class="cs-item"><div class="cs-num">${det!.resumo.peso_total.toFixed(1)} kg</div><div class="cs-txt">Peso</div></div>` : ''}
          ${temOcorrencia ? `<div class="cs-item danger"><div class="cs-num">${numOcorrencias}</div><div class="cs-txt">Ocorrências</div></div>` : ''}
        </div>

        ${pedidosStr ? `
        <div class="cracha-pedidos">
          <span class="cp-label">PEDIDOS:</span>
          <span class="cp-list">${pedidosStr}</span>
        </div>` : ''}

        <div class="cracha-bottom">
          <span>${operacao.conferente_nome ? `Conferente: ${operacao.conferente_nome}` : ''}</span>
          <span>${formatDateTime(new Date().toISOString())}</span>
        </div>
      </div>
    </div>
  `}).join('');

  const styles = `
    @page { size: A4 landscape; margin: 0; }
    html, body { width: 100%; padding: 0; margin: 0; }
    .cracha-page {
      page-break-after: always; width: 100%; height: 100vh;
      display: flex; padding: 0; margin: 0;
    }
    .cracha-page:last-child { page-break-after: auto; }
    .cracha {
      border: 5px solid #04364F; padding: 18px 24px;
      width: 100%; height: 100%; display: flex; flex-direction: column;
    }
    .cracha.com-ocorrencia { border-color: #e74c3c; }
    .cracha-top {
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 4px solid #04364F; padding-bottom: 8px; margin-bottom: 8px;
      flex-shrink: 0;
    }
    .com-ocorrencia .cracha-top { border-bottom-color: #e74c3c; }
    .cracha-top-left { display: flex; align-items: center; gap: 12px; }
    .cracha-logo { max-width: 90px; max-height: 55px; object-fit: contain; }
    .cracha-empresa { font-size: 1.4rem; font-weight: 700; color: #04364F; }
    .cracha-top-right { text-align: right; }
    .cracha-op { font-size: 1.8rem; font-weight: 900; color: #04364F; }
    .cracha-data { font-size: 1.1rem; color: #444; font-weight: 600; }

    .cracha-body { display: flex; gap: 24px; flex: 1; align-items: center; min-height: 0; overflow: hidden; }
    .cracha-center {
      flex: 0 0 320px; display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      border-right: 4px dashed #bbb; padding-right: 24px;
    }
    .cracha-label-small { font-size: 1.6rem; font-weight: 800; color: #7f8c8d; letter-spacing: 6px; }
    .cracha-number {
      font-size: 12rem; font-weight: 900; color: #04364F;
      line-height: 1; margin: 4px 0;
    }
    .com-ocorrencia .cracha-number { color: #e74c3c; }
    .cracha-tipo-badge {
      padding: 8px 32px; border-radius: 8px; font-weight: 800;
      font-size: 1.4rem; letter-spacing: 4px;
    }
    .cracha-tipo-badge.normal { background: #d1ecf1; color: #0c5460; }
    .cracha-tipo-badge.agend { background: #fde8d0; color: #a04000; }

    .cracha-info-grid {
      flex: 1; display: grid; grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    .cracha-info-box { display: flex; flex-direction: column; gap: 2px; }
    .cracha-info-box-area { position: relative; }
    .ci-label { font-size: 1rem; font-weight: 700; color: #7f8c8d; letter-spacing: 1px; }
    .ci-value { font-size: 2.2rem; font-weight: 800; color: #2c3e50; }

    .cracha-qr { width: 90px; height: 90px; object-fit: contain; }
    .cracha-qr-palete { margin-top: 8px; }
    .cracha-qr-area { margin-top: 4px; }

    .cracha-stats {
      display: flex; gap: 12px; padding: 10px 0;
      border-top: 4px solid #ddd; border-bottom: 4px solid #ddd;
      margin: 8px 0; flex-shrink: 0;
    }
    .cs-item {
      flex: 1; text-align: center; background: #f8f9fa;
      border-radius: 8px; padding: 10px 8px; border: 2px solid #e0e0e0;
    }
    .cs-item.warn { background: #fff3cd; border-color: #ffc107; }
    .cs-item.danger { background: #f8d7da; border-color: #f5c6cb; }
    .cs-num { font-size: 1.8rem; font-weight: 900; color: #2c3e50; }
    .cs-item.warn .cs-num { color: #856404; }
    .cs-item.danger .cs-num { color: #721c24; }
    .cs-txt { font-size: 0.9rem; font-weight: 700; color: #7f8c8d; text-transform: uppercase; letter-spacing: 0.5px; }

    .cracha-pedidos {
      margin-bottom: 4px; font-size: 1.1rem; flex-shrink: 0;
    }
    .cp-label { font-weight: 700; color: #7f8c8d; font-size: 1rem; letter-spacing: 1px; }
    .cp-list { color: #2c3e50; font-weight: 600; margin-left: 8px; }

    .cracha-bottom {
      display: flex; justify-content: space-between;
      font-size: 0.9rem; color: #777; flex-shrink: 0;
      border-top: 3px solid #04364F; padding-top: 6px;
    }
    .com-ocorrencia .cracha-bottom { border-top-color: #e74c3c; }
  `;

  const body = crachas;
  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// RELATÓRIO 1: PALETES CARREGADOS
// =============================================

export const printRelatorioPaletesCarregados = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  let totalPedidos = 0, totalLeituras = 0, totalOk = 0, totalOcorrencias = 0, pesoTotal = 0;
  detalhados.forEach(d => {
    totalPedidos += d.resumo?.total_pedidos || 0;
    totalLeituras += d.resumo?.total_leituras || 0;
    totalOk += d.resumo?.leituras_ok || 0;
    totalOcorrencias += d.ocorrencias?.length || 0;
    pesoTotal += d.resumo?.peso_total || 0;
  });

  const rows = paletes.map((p, i) => {
    const det = detalheMap.get(p.id);
    const numOcorrencias = det?.ocorrencias?.length || 0;
    const temOcorrencia = numOcorrencias > 0;
    return `
    <tr${temOcorrencia ? ' style="background:#fff8f0"' : ''}>
      <td style="text-align:center">${i + 1}</td>
      <td style="text-align:center;font-weight:700">${p.numero_palete}</td>
      <td>${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}</td>
      <td>${p.area_regiao || '-'}</td>
      <td>${p.doca || '-'}</td>
      <td style="text-align:center">${p.tipo_palete === 'agendamento'
        ? '<span style="background:#fde8d0;color:#a04000;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Agend.</span>'
        : '<span style="background:#d1ecf1;color:#0c5460;padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Normal</span>'}</td>
      <td style="text-align:center">${det?.resumo?.total_pedidos || 0}</td>
      <td style="text-align:center">${det?.resumo?.leituras_ok || 0}/${det?.resumo?.total_leituras || 0}</td>
      <td style="text-align:center">${det?.resumo?.peso_total ? det.resumo.peso_total.toFixed(1) : '-'}</td>
      <td style="text-align:center">${temOcorrencia
        ? `<span style="background:#f8d7da;color:#721c24;padding:2px 6px;border-radius:4px;font-size:0.75rem;font-weight:600">${numOcorrencias} ocorr.</span>`
        : '<span style="background:#d4edda;color:#155724;padding:2px 6px;border-radius:4px;font-size:0.75rem;font-weight:600">OK</span>'}</td>
      <td style="width:80px"></td>
    </tr>
  `}).join('');

  const styles = `
    @page { size: A4 landscape; margin: 1.2cm; }
    .totals {
      display: flex; gap: 14px; margin: 1rem 0; flex-wrap: wrap;
      font-size: 0.8rem; font-weight: 600;
    }
    .totals span { background: #f0f4f8; padding: 6px 12px; border-radius: 6px; }
    .totals .alert { background: #f8d7da; color: #721c24; }
    .sign-area {
      display: flex; justify-content: space-between; margin-top: 2.5rem;
      page-break-inside: avoid;
    }
    .sign-box { text-align: center; width: 200px; }
    .sign-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 4px; font-size: 0.75rem; color: #666; }
  `;

  const body = `
    ${buildHeader('Relatório de Paletes Carregados', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}

    <div class="totals">
      <span>Paletes: ${paletes.length}</span>
      <span>Áreas: ${new Set(paletes.map(p => p.area_entrega_id)).size}</span>
      <span>Docas: ${new Set(paletes.filter(p => p.doca).map(p => p.doca)).size}</span>
      <span>Pedidos: ${totalPedidos}</span>
      <span>Etiquetas: ${totalOk}/${totalLeituras}</span>
      ${pesoTotal > 0 ? `<span>Peso: ${pesoTotal.toFixed(1)} kg</span>` : ''}
      ${totalOcorrencias > 0 ? `<span class="alert">Ocorrências: ${totalOcorrencias}</span>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:30px;text-align:center">#</th>
          <th style="width:55px;text-align:center">Palete</th>
          <th>Área de Entrega</th>
          <th>Região</th>
          <th>Doca</th>
          <th style="width:65px;text-align:center">Tipo</th>
          <th style="width:55px;text-align:center">Pedidos</th>
          <th style="width:75px;text-align:center">Etiquetas</th>
          <th style="width:55px;text-align:center">Peso</th>
          <th style="width:75px;text-align:center">Status</th>
          <th style="width:80px;text-align:center">Visto</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="sign-area">
      <div class="sign-box"><div class="sign-line">Conferente</div></div>
      <div class="sign-box"><div class="sign-line">Motorista</div></div>
      <div class="sign-box"><div class="sign-line">Responsável</div></div>
    </div>

    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// RELATÓRIO 2: PEDIDOS E VOLUMES FALTANTES
// =============================================

export const printRelatorioPedidosVolumesFaltantes = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  let totalVolumesFaltantes = 0;
  let totalOcorrencias = 0;
  const rows: string[] = [];
  let idx = 0;

  paletes.forEach(p => {
    const det = detalheMap.get(p.id);
    const pedidos = det?.pedidos || [];
    const ocorrencias = det?.ocorrencias || [];
    const etiquetasPendentes = det?.etiquetas_pendentes || [];

    // Agrupar ocorrências por numero_pedido
    const ocorrenciasPorPedido: Record<string, typeof ocorrencias> = {};
    const ocorrenciasSemPedido: typeof ocorrencias = [];
    ocorrencias.forEach(o => {
      if (o.numero_pedido) {
        if (!ocorrenciasPorPedido[o.numero_pedido]) ocorrenciasPorPedido[o.numero_pedido] = [];
        ocorrenciasPorPedido[o.numero_pedido].push(o);
      } else {
        ocorrenciasSemPedido.push(o);
      }
    });

    // Agrupar etiquetas pendentes por numero_pedido
    const pendentesPorPedido: Record<string, EtiquetaPendente[]> = {};
    etiquetasPendentes.forEach(e => {
      if (e.numero_pedido) {
        if (!pendentesPorPedido[e.numero_pedido]) pendentesPorPedido[e.numero_pedido] = [];
        pendentesPorPedido[e.numero_pedido].push(e);
      }
    });

    // Pedidos com volumes faltantes OU com ocorrências associadas (descontando removidos)
    const faltantes = pedidos.filter(pd => {
      const faltantesEfetivos = calcularFaltantesEfetivos(pd);
      return faltantesEfetivos > 0;
    });
    const pedidosComOcorrencia = pedidos.filter(pd => {
      const faltantesEfetivos = calcularFaltantesEfetivos(pd);
      return faltantesEfetivos <= 0 && ocorrenciasPorPedido[pd.numero_pedido];
    });
    const todosRelevantes = [...faltantes, ...pedidosComOcorrencia];
    const temDados = todosRelevantes.length > 0 || ocorrenciasSemPedido.length > 0;

    if (temDados) {
      // Cabeçalho do palete
      rows.push(`
        <tr style="background:#f0f4f8">
          <td colspan="8" style="font-weight:700;font-size:0.85rem;padding:8px 6px">
            <i class="fas fa-pallet" style="margin-right:6px;color:#e74c3c"></i>
            Palete #${p.numero_palete} — ${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}
            ${p.doca ? ` — Doca: ${p.doca}` : ''}
          </td>
        </tr>
      `);

      // Volumes faltantes e pedidos com ocorrências (descontando removidos)
      todosRelevantes.forEach(pd => {
        idx++;
        const qtdFaltante = Math.max(0, calcularFaltantesEfetivos(pd));
        if (qtdFaltante > 0) totalVolumesFaltantes += qtdFaltante;

        // Listar etiquetas que foram lidas deste pedido
        const etiqOk = pd.etiquetas.filter(e => e.resultado === 'ok');
        const etiqDup = pd.etiquetas.filter(e => e.resultado === 'duplicado');
        const ocsPedido = ocorrenciasPorPedido[pd.numero_pedido] || [];
        const etqPendentes = pendentesPorPedido[pd.numero_pedido] || [];

        rows.push(`
          <tr>
            <td style="text-align:center">${idx}</td>
            <td style="font-weight:700">${pd.numero_pedido}</td>
            <td style="text-align:center">${pd.total_volumes_esperado}</td>
            <td style="text-align:center">${pd.total_lido}</td>
            <td style="text-align:center;font-weight:700;${qtdFaltante > 0 ? 'color:#e74c3c' : 'color:#27ae60'};font-size:1rem">${qtdFaltante > 0 ? qtdFaltante : '0'}</td>
            <td style="text-align:center">${etiqDup.length > 0 ? `<span style="color:#e67e22">${etiqDup.length} duplicada(s)</span>` : '-'}</td>
            <td style="text-align:center">${pd.total_removidos > 0 ? `${pd.total_removidos} removido(s)` : '-'}</td>
            <td>${ocsPedido.length > 0
              ? `<span style="color:#856404;font-weight:600">${ocsPedido.length} ocorrência(s)</span>`
              : etiqOk.length > 0
                ? `${etiqOk.length} volume(s) confirmado(s)`
                : 'Nenhum volume lido'}</td>
          </tr>
        `);

        // Etiquetas pendentes deste pedido
        if (etqPendentes.length > 0) {
          rows.push(`
            <tr style="background:#fef3f3">
              <td></td>
              <td colspan="7" style="font-size:0.8rem;padding:4px 6px 2px 20px;color:#c0392b;font-weight:600">
                <i class="fas fa-tag" style="margin-right:4px"></i>
                Etiquetas pendentes (${etqPendentes.length}):
              </td>
            </tr>
          `);
          etqPendentes.forEach(etq => {
            rows.push(`
              <tr style="background:#fef3f3">
                <td></td>
                <td style="font-size:0.75rem;padding-left:30px;color:#555;white-space:nowrap">
                  ${etq.volume_atual ? `Vol. ${etq.volume_atual}${etq.total_volumes_pedido ? `/${etq.total_volumes_pedido}` : ''}` : '-'}
                </td>
                <td colspan="6" style="font-size:0.75rem;color:#555;white-space:nowrap">
                  ${etq.codigo_barras || '<em>sem código</em>'}
                  ${etq.numero_nfe ? ` &nbsp;|&nbsp; NF-e: ${etq.numero_nfe}` : ''}
                  ${etq.destinatario_nome ? ` — ${etq.destinatario_nome}` : ''}
                </td>
              </tr>
            `);
          });
        }

        // Ocorrências vinculadas a este pedido
        ocsPedido.forEach(o => {
          totalOcorrencias++;
          rows.push(`
            <tr style="background:#fff8f0">
              <td></td>
              <td colspan="4" style="font-size:0.8rem;color:#856404;padding-left:20px">
                <i class="fas fa-exclamation-circle" style="margin-right:4px"></i>
                <span style="font-weight:600">${o.tipo_nome}</span>
                ${o.quantidade_divergente > 0 ? ` — Qtd: ${o.quantidade_divergente}` : ''}
                <span style="color:#999;margin-left:8px">${formatDateTime(o.criado_em)}</span>
              </td>
              <td colspan="3" style="font-size:0.8rem;color:#555">${o.justificativa || '-'}</td>
            </tr>
          `);
        });
      });

      // Ocorrências sem pedido associado
      ocorrenciasSemPedido.forEach(o => {
        idx++;
        totalOcorrencias++;
        rows.push(`
          <tr style="background:#fff8f0">
            <td style="text-align:center">${idx}</td>
            <td>-</td>
            <td colspan="3" style="font-weight:600;color:#856404">
              ${o.tipo_nome} (${o.categoria})
              ${o.quantidade_divergente > 0 ? ` — Qtd divergente: ${o.quantidade_divergente}` : ''}
            </td>
            <td colspan="2" style="font-size:0.8rem">${formatDateTime(o.criado_em)}</td>
            <td style="font-size:0.8rem">${o.justificativa || '-'}</td>
          </tr>
        `);
      });
    }
  });

  // Se não tem dados, mostrar mensagem
  if (rows.length === 0) {
    rows.push(`
      <tr>
        <td colspan="8" style="text-align:center;padding:20px;color:#27ae60;font-weight:600">
          <i class="fas fa-check-circle" style="margin-right:6px"></i>
          Nenhum pedido ou volume faltante identificado nesta operação.
        </td>
      </tr>
    `);
  }

  const styles = `
    @page { size: A4 landscape; margin: 1.2cm; }
    .summary-bar {
      display: flex; gap: 14px; margin-bottom: 1rem; flex-wrap: wrap;
      font-size: 0.8rem; font-weight: 600;
    }
    .summary-bar span { background: #f0f4f8; padding: 6px 12px; border-radius: 6px; }
    .summary-bar .alert { background: #f8d7da; color: #721c24; }
    .summary-bar .success { background: #d4edda; color: #155724; }
    .sign-area {
      display: flex; justify-content: space-between; margin-top: 2rem;
      page-break-inside: avoid;
    }
    .sign-box { text-align: center; width: 200px; }
    .sign-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 4px; font-size: 0.75rem; color: #666; }
  `;

  const body = `
    ${buildHeader('Relatório de Pedidos e Volumes Faltantes', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}

    <div class="summary-bar">
      <span>Paletes: ${paletes.length}</span>
      ${totalVolumesFaltantes > 0
        ? `<span class="alert">Volumes faltantes: ${totalVolumesFaltantes}</span>`
        : `<span class="success">Todos os volumes conferidos</span>`}
      ${totalOcorrencias > 0 ? `<span class="alert">Ocorrências: ${totalOcorrencias}</span>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:30px;text-align:center">#</th>
          <th>Nº Pedido</th>
          <th style="width:70px;text-align:center">Esperado</th>
          <th style="width:55px;text-align:center">Lido</th>
          <th style="width:65px;text-align:center">Faltante</th>
          <th style="width:90px;text-align:center">Duplicados</th>
          <th style="width:80px;text-align:center">Removidos</th>
          <th>Observações</th>
        </tr>
      </thead>
      <tbody>${rows.join('')}</tbody>
    </table>

    <div class="sign-area">
      <div class="sign-box"><div class="sign-line">Conferente</div></div>
      <div class="sign-box"><div class="sign-line">Supervisor</div></div>
    </div>

    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// RELATÓRIO 3: PEDIDOS BIPADOS
// =============================================

export const printRelatorioPedidosBipados = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  let totalBipados = 0, totalEsperado = 0, totalDuplicados = 0;
  const rows: string[] = [];
  let idx = 0;

  paletes.forEach(p => {
    const det = detalheMap.get(p.id);
    const pedidos = det?.pedidos || [];

    if (pedidos.length > 0) {
      // Cabeçalho do palete
      rows.push(`
        <tr style="background:#f0f4f8">
          <td colspan="9" style="font-weight:700;font-size:0.85rem;padding:8px 6px">
            <i class="fas fa-pallet" style="margin-right:6px;color:#3498db"></i>
            Palete #${p.numero_palete} — ${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}
            ${p.doca ? ` — Doca: ${p.doca}` : ''}
            <span style="float:right;font-size:0.75rem;color:#7f8c8d">
              ${det?.resumo?.leituras_ok || 0}/${det?.resumo?.total_leituras || 0} etiquetas
              ${(det?.resumo?.leituras_duplicadas || 0) > 0 ? ` · ${det!.resumo.leituras_duplicadas} dup.` : ''}
            </span>
          </td>
        </tr>
      `);

      pedidos.forEach(pd => {
        idx++;
        const completo = (pd.total_lido + calcularRemovidosUnicos(pd.etiquetas)) >= pd.total_volumes_esperado && pd.total_volumes_esperado > 0;
        totalBipados += pd.total_lido;
        totalEsperado += pd.total_volumes_esperado;
        totalDuplicados += pd.total_duplicados;

        // Contagem de resultados das etiquetas
        const etiqOk = pd.etiquetas.filter(e => e.resultado === 'ok').length;
        const etiqDup = pd.etiquetas.filter(e => e.resultado === 'duplicado').length;
        const etiqNaoEnc = pd.etiquetas.filter(e => e.resultado === 'nao_encontrado').length;
        const etiqRemov = pd.etiquetas.filter(e => e.resultado === 'removido').length;

        rows.push(`
          <tr>
            <td style="text-align:center">${idx}</td>
            <td style="font-weight:700">${pd.numero_pedido}</td>
            <td style="text-align:center">${pd.total_volumes_esperado}</td>
            <td style="text-align:center;font-weight:700;color:${completo ? '#27ae60' : '#e74c3c'}">${pd.total_lido}</td>
            <td style="text-align:center">
              ${etiqOk > 0 ? `<span style="color:#27ae60;font-weight:600">${etiqOk} ok</span>` : ''}
              ${etiqDup > 0 ? ` <span style="color:#e67e22;font-weight:600">${etiqDup} dup</span>` : ''}
              ${etiqNaoEnc > 0 ? ` <span style="color:#e74c3c;font-weight:600">${etiqNaoEnc} n/e</span>` : ''}
              ${etiqRemov > 0 ? ` <span style="color:#7f8c8d">${etiqRemov} rem</span>` : ''}
              ${etiqOk === 0 && etiqDup === 0 && etiqNaoEnc === 0 && etiqRemov === 0 ? '-' : ''}
            </td>
            <td style="text-align:center">${pd.total_duplicados > 0 ? `<span style="color:#e67e22;font-weight:600">${pd.total_duplicados}</span>` : '0'}</td>
            <td style="text-align:center">${pd.total_removidos > 0 ? pd.total_removidos : '0'}</td>
            <td style="text-align:center;font-size:0.85rem">${
              completo
                ? '<span style="color:#27ae60;font-weight:700"><i class="fas fa-check-circle"></i> Completo</span>'
                : `<span style="color:#e74c3c;font-weight:600">Faltam ${Math.max(0, calcularFaltantesEfetivos(pd))}</span>`
            }</td>
            <td style="font-size:0.7rem;color:#999">${pd.etiquetas.length > 0 ? formatDateTime(pd.etiquetas[pd.etiquetas.length - 1].lido_em) : '-'}</td>
          </tr>
        `);
      });
    } else if ((det?.resumo?.total_leituras || 0) > 0) {
      // Palete com leituras mas sem pedidos vinculados
      rows.push(`
        <tr style="background:#fff8f0">
          <td colspan="9" style="padding:6px;font-size:0.8rem">
            <i class="fas fa-pallet" style="margin-right:6px;color:#e67e22"></i>
            Palete #${p.numero_palete} — ${det!.resumo.total_leituras} etiquetas lidas sem pedidos vinculados
          </td>
        </tr>
      `);
    }
  });

  if (rows.length === 0) {
    rows.push(`
      <tr>
        <td colspan="9" style="text-align:center;padding:20px;color:#7f8c8d">
          Nenhum pedido bipado encontrado nesta operação.
        </td>
      </tr>
    `);
  }

  const styles = `
    @page { size: A4 landscape; margin: 1.2cm; }
    .summary-bar {
      display: flex; gap: 14px; margin-bottom: 1rem; flex-wrap: wrap;
      font-size: 0.8rem; font-weight: 600;
    }
    .summary-bar span { background: #f0f4f8; padding: 6px 12px; border-radius: 6px; }
    .summary-bar .success { background: #d4edda; color: #155724; }
    .summary-bar .warn { background: #fff3cd; color: #856404; }
    .sign-area {
      display: flex; justify-content: space-between; margin-top: 2rem;
      page-break-inside: avoid;
    }
    .sign-box { text-align: center; width: 200px; }
    .sign-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 4px; font-size: 0.75rem; color: #666; }
  `;

  const body = `
    ${buildHeader('Relatório de Pedidos Bipados', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}

    <div class="summary-bar">
      <span>Paletes: ${paletes.length}</span>
      <span>Volumes bipados: ${totalBipados}/${totalEsperado}</span>
      ${totalDuplicados > 0 ? `<span class="warn">Duplicados: ${totalDuplicados}</span>` : ''}
      ${totalBipados >= totalEsperado && totalEsperado > 0 ? `<span class="success">Conferência completa</span>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:30px;text-align:center">#</th>
          <th>Nº Pedido</th>
          <th style="width:65px;text-align:center">Esperado</th>
          <th style="width:55px;text-align:center">Lido</th>
          <th style="width:130px;text-align:center">Detalhes Etiquetas</th>
          <th style="width:60px;text-align:center">Duplic.</th>
          <th style="width:55px;text-align:center">Remov.</th>
          <th style="width:90px;text-align:center">Status</th>
          <th style="width:110px;text-align:center">Última Leitura</th>
        </tr>
      </thead>
      <tbody>${rows.join('')}</tbody>
    </table>

    <div class="sign-area">
      <div class="sign-box"><div class="sign-line">Conferente</div></div>
      <div class="sign-box"><div class="sign-line">Supervisor</div></div>
    </div>

    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};

// =============================================
// RELATÓRIO 4: VOLUMES FALTANTES
// =============================================

export const printRelatorioVolumesFaltantes = async ({ operacao, paletes }: PrintConfig) => {
  const empresa = await fetchEmpresaData();
  const user = getUser();
  const logoSrc = empresa?.logomarca_base64 || '/images/logo-confereai.png';
  const agora = formatDateTime(new Date().toISOString());

  // Buscar dados detalhados
  const detalhados = await fetchPaletesDetalhados(paletes.map(p => p.id));
  const detalheMap = new Map(detalhados.map(d => [d.id, d]));

  let totalVolumesFaltantes = 0;
  const rows: string[] = [];
  let idx = 0;

  paletes.forEach(p => {
    const det = detalheMap.get(p.id);
    const etiquetasPendentes = det?.etiquetas_pendentes || [];
    const pedidos = det?.pedidos || [];

    // Pedidos com volumes faltantes (descontando volumes removidos intencionalmente)
    const pedidosComFaltantes = pedidos.filter(
      pd => {
        if (pd.numero_pedido === 'Sem Pedido') return false;
        const faltantesEfetivos = calcularFaltantesEfetivos(pd);
        return faltantesEfetivos > 0;
      }
    );

    // Calcular volumes faltantes baseado nos pedidos (descontando removidos)
    const totalFaltantesPedidos = pedidosComFaltantes.reduce(
      (acc, pd) => acc + (calcularFaltantesEfetivos(pd)), 0
    );

    // Se não há etiquetas pendentes E não há pedidos com faltantes, pular
    if (etiquetasPendentes.length === 0 && totalFaltantesPedidos === 0) return;

    // Determinar quais pedidos já estão cobertos por etiquetas_pendentes
    const pedidosCobertos = new Set(etiquetasPendentes.map(e => e.numero_pedido));

    // Pedidos com faltantes que NÃO têm etiquetas_pendentes correspondentes (fallback)
    const pedidosSemEtiqueta = pedidosComFaltantes.filter(pd => !pedidosCobertos.has(pd.numero_pedido));

    // Calcular total de faltantes: usar etiquetas quando disponíveis + pedidos não cobertos
    const totalFaltantesEtiquetas = etiquetasPendentes.length;
    const totalFaltantesSemEtiqueta = pedidosSemEtiqueta.reduce(
      (acc, pd) => acc + (calcularFaltantesEfetivos(pd)), 0
    );
    const totalFaltantesPalete = totalFaltantesEtiquetas + totalFaltantesSemEtiqueta;
    totalVolumesFaltantes += totalFaltantesPalete;

    // Cabeçalho do palete
    rows.push(`
      <tr style="background:#f0f4f8">
        <td colspan="6" style="font-weight:700;font-size:0.85rem;padding:8px 6px">
          <i class="fas fa-pallet" style="margin-right:6px;color:#e74c3c"></i>
          Palete #${p.numero_palete} — ${p.area_codigo ? `${p.area_codigo} (${p.area_nome || ''})` : (p.area_nome || `#${p.area_entrega_id}`)}
          ${p.doca ? ` — Doca: ${p.doca}` : ''}
          <span style="float:right;font-size:0.75rem;color:#e74c3c;font-weight:600">${totalFaltantesPalete} volume(s) faltante(s)</span>
        </td>
      </tr>
    `);

    // Lista de etiquetas pendentes (quando disponíveis)
    etiquetasPendentes.forEach(etq => {
      idx++;
      rows.push(`
        <tr>
          <td style="text-align:center">${idx}</td>
          <td style="white-space:nowrap">${etq.codigo_barras || '<em>sem código</em>'}</td>
          <td style="font-weight:600">${etq.numero_pedido || '-'}</td>
          <td style="text-align:center">${etq.volume_atual ? `${etq.volume_atual}${etq.total_volumes_pedido ? `/${etq.total_volumes_pedido}` : ''}` : '-'}</td>
          <td>${etq.numero_nfe || '-'}</td>
          <td>${etq.destinatario_nome || '-'}</td>
        </tr>
      `);
    });

    // Fallback: pedidos com faltantes sem etiquetas_pendentes correspondentes
    // Mostra informações baseadas nos dados do pedido (descontando removidos)
    pedidosSemEtiqueta.forEach(pd => {
      const qtdFaltante = calcularFaltantesEfetivos(pd);
      if (qtdFaltante <= 0) return;

      // Identificar quais volumes foram lidos (ok) ou removidos para excluí-los dos faltantes
      const volumesExcluidos = new Set<number>();
      pd.etiquetas.forEach((e) => {
        if (e.resultado === 'ok' || e.resultado === 'removido') {
          const dados = (e.dados_decodificados || {}) as Record<string, unknown>;
          const vol = dados.volume_atual || dados.volume;
          if (vol) volumesExcluidos.add(Number(vol));
        }
      });

      // Gerar linhas para cada volume faltante
      let volumesMostrados = 0;
      if (pd.total_volumes_esperado > 0 && volumesExcluidos.size > 0) {
        // Conseguimos deduzir quais volumes específicos faltam
        for (let v = 1; v <= pd.total_volumes_esperado; v++) {
          if (!volumesExcluidos.has(v)) {
            idx++;
            volumesMostrados++;
            rows.push(`
              <tr>
                <td style="text-align:center">${idx}</td>
                <td style="white-space:nowrap;color:#999"><em>etiqueta não importada</em></td>
                <td style="font-weight:600">${pd.numero_pedido}</td>
                <td style="text-align:center">${v}/${pd.total_volumes_esperado}</td>
                <td>-</td>
                <td>-</td>
              </tr>
            `);
          }
        }
      }

      // Se não conseguimos deduzir volumes específicos, mostrar linha resumo
      if (volumesMostrados === 0) {
        for (let i = 0; i < qtdFaltante; i++) {
          idx++;
          rows.push(`
            <tr>
              <td style="text-align:center">${idx}</td>
              <td style="white-space:nowrap;color:#999"><em>etiqueta não importada</em></td>
              <td style="font-weight:600">${pd.numero_pedido}</td>
              <td style="text-align:center">${pd.total_lido + i + 1 <= pd.total_volumes_esperado ? `?/${pd.total_volumes_esperado}` : '-'}</td>
              <td>-</td>
              <td>-</td>
            </tr>
          `);
        }
      }
    });
  });

  if (rows.length === 0) {
    rows.push(`
      <tr>
        <td colspan="6" style="text-align:center;padding:20px;color:#27ae60;font-weight:600">
          <i class="fas fa-check-circle" style="margin-right:6px"></i>
          Nenhum volume faltante identificado nesta operação. Todos os volumes foram conferidos com sucesso.
        </td>
      </tr>
    `);
  }

  const styles = `
    @page { size: A4 landscape; margin: 1.2cm; }
    .summary-bar {
      display: flex; gap: 14px; margin-bottom: 1rem; flex-wrap: wrap;
      font-size: 0.8rem; font-weight: 600;
    }
    .summary-bar span { background: #f0f4f8; padding: 6px 12px; border-radius: 6px; }
    .summary-bar .alert { background: #f8d7da; color: #721c24; }
    .summary-bar .success { background: #d4edda; color: #155724; }
    .sign-area {
      display: flex; justify-content: space-between; margin-top: 2rem;
      page-break-inside: avoid;
    }
    .sign-box { text-align: center; width: 200px; }
    .sign-line { border-top: 1px solid #333; margin-top: 40px; padding-top: 4px; font-size: 0.75rem; color: #666; }
  `;

  const body = `
    ${buildHeader('Relatório de Volumes Faltantes', `Operação #${operacao.numero_operacao}`, logoSrc, user?.nome || '', agora)}
    ${buildInfoBar(operacao)}

    <div class="summary-bar">
      <span>Paletes: ${paletes.length}</span>
      ${totalVolumesFaltantes > 0
        ? `<span class="alert">Total de volumes faltantes: ${totalVolumesFaltantes}</span>`
        : `<span class="success">Todos os volumes conferidos</span>`}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:30px;text-align:center">#</th>
          <th>Código de Barras</th>
          <th>Nº Pedido</th>
          <th style="width:80px;text-align:center">Volume</th>
          <th>NF-e</th>
          <th>Destinatário</th>
        </tr>
      </thead>
      <tbody>${rows.join('')}</tbody>
    </table>

    <div class="sign-area">
      <div class="sign-box"><div class="sign-line">Conferente</div></div>
      <div class="sign-box"><div class="sign-line">Supervisor</div></div>
    </div>

    ${buildFooter(empresa)}
  `;

  openPrintWindow(buildDoc(styles, body));
};
